import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { fromCamelCase } from "utils/handleCamelse";
import { Typography } from "@mui/material";
import { isValidJSON } from "utils/isValidJson";

export default function VitalSignsTable({ data, fromSummary = false }) {
  const [dynamicFieldsHeader, setdynamicFieldsHeader] = React.useState(
    new Set()
  );
  const [fields, setfields] = React.useState([]);

  const isEmpty = () => {
    return !fields.length && !Array.from(dynamicFieldsHeader).length;
  };
  const isFieldsEmpty = (field) => {
    const { patient, ...filteredObj } = field;

    // Check if all values are either 0 or ""
    const allEmpty = Object.values(filteredObj).every(
      (value) => value === 0 || value === "" || value === undefined
    );
    return allEmpty;
  };

  React.useMemo(() => {
    const fields = new Set();

    if (data?.length) {
      data.forEach((_data) => {
        let dynamicFields = {};

        if (
          _data?.vitalSigns?.dynamicFields &&
          isValidJSON(_data?.vitalSigns?.dynamicFields)
        )
          dynamicFields = JSON.parse(_data?.vitalSigns?.dynamicFields || {});
        Object.keys(dynamicFields).forEach((key) => {
          fields.add(fromCamelCase(key));
        });
      });

      setdynamicFieldsHeader(fields);
    }

    if (data?.length) {
      const otherFields = [];
      data.forEach((_data) => {
        const obj = {};
        _data?.vitalSigns &&
          Object.keys(_data?.vitalSigns).forEach((key) => {
            if (
              key === "_id" ||
              key === "updatedAt" ||
              key === "createdAt" ||
              key === "id"
            )
              return;
            if (key !== "dynamicFields") obj[key] = _data?.vitalSigns?.[key];

            let dynamicFields = {};

            if (isValidJSON(_data?.vitalSigns?.dynamicFields))
              dynamicFields = JSON.parse(
                _data?.vitalSigns?.dynamicFields || {}
              );

            if (Object.keys(dynamicFields).length)
              Object.keys(dynamicFields).forEach((key) => {
                obj[fromCamelCase(key)] =
                  dynamicFields?.[key]?.["value"] || dynamicFields[key];
              });
          });

        if (Object.keys(obj).length) otherFields.push(obj);
      });

      setfields(otherFields);
    }
  }, [data]);

  return (
    <>
      {isEmpty() ? (
        <Paper sx={{ p: 2 }}>
          <Typography>No data</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow
                sx={{ backgroundColor: !fromSummary ? "background.light" : "" }}
              >
                <TableCell sx={{ fontWeight: "bold" }} align="left">
                  Height
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", textWrap: "nowrap" }}
                  align="left"
                >
                  Blood Pressure
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", textWrap: "nowrap" }}
                  align="left"
                >
                  Heart Rate
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="left">
                  Temperature
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", textWrap: "nowrap" }}
                  align="left"
                >
                  Glucose Level
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="left">
                  Weight
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", textWrap: "nowrap" }}
                  align="left"
                >
                  Respiratory Rate
                </TableCell>

                <TableCell
                  sx={{ fontWeight: "bold", textWrap: "nowrap" }}
                  align="left"
                >
                  Oxygen Saturation
                </TableCell>
                {
                  //for dynamic fields
                  Array.from(dynamicFieldsHeader).map((field) => (
                    <TableCell
                      sx={{ fontWeight: "bold", textWrap: "nowrap" }}
                      align="left"
                      key={field}
                    >
                      {field}
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>

            <TableBody>
              {fields?.map((d, idx) =>
                !isFieldsEmpty(d) ? (
                  <TableRow
                    key={idx}
                    sx={{ border: 0, borderBottom: "none !important" }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.height || "-"}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.systolicBloodPressure || 0}
                      {"/"}
                      {d?.diastolicBloodPressure || 0}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {" "}
                      {d?.heartRate || "-"}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.temperature || "-"}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.glucose || "-"}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.weight || "-"}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.respiratoryRate || "-"}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{ border: 0, borderBottom: "none !important" }}
                    >
                      {d?.oxygenSaturation || "-"}
                    </TableCell>
                    {Array.from(dynamicFieldsHeader).map((fieldHeader) =>
                      !isFieldsEmpty({ [fieldHeader]: d?.[fieldHeader] }) ? (
                        <React.Fragment key={fieldHeader}>
                          <TableCell
                            align="left"
                            sx={{
                              border: 0,
                              borderBottom: "none !important",
                            }}
                          >
                            {d?.[fieldHeader] || "-"}
                          </TableCell>
                        </React.Fragment>
                      ) : null
                    )}
                  </TableRow>
                ) : null
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
